'use client'

import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { use, useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import { useMediaQuery } from 'usehooks-ts'

import SortByMenu from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/SortByMenu'
import ExploreViverseTags, {
  ExploreViverseTagsSkeleton,
} from '@/app/[locale]/(worlds)/components/ExploreViverseTags'
import ViverseButton from '@/components/ViverseButton'
import ViverseWorldCard from '@/components/ViverseWorldCard'
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton'
import IconGamepad from '@/components/assets/icon_gamepad.svg'
import { Separator } from '@/components/ui/separator'
import {
  BI_CATEGORY_SORT_AND_SEARCH,
  BI_EVENT_FEATURED,
  BI_EVENT_LATEST,
  BI_EVENT_MOST_LIKED,
  BI_EVENT_MOST_VISITED,
  BI_EVENT_RELEASE_DATE,
} from '@/constants/bi-sdk'
import { EXPLORE_GAMES_LIMIT } from '@/constants/games'
import { SEARCH_PARAM_KEYS } from '@/constants/search'
import { useBISdkContext } from '@/context/BISdkContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useLayoutContext } from '@/context/LayoutContext'
import { useUAContext } from '@/context/UAContext'
import { useBrowserLocale } from '@/hooks/useBrowserLocale'
import useHash from '@/hooks/useHash'
import { useLoadMore } from '@/hooks/useLoadMore'
import {
  skeletonState,
  useFallbackSkeletons,
  useSavedPages,
  useScrollRestorationSWR,
} from '@/hooks/useScrollRestoration'
import { useSearchFilters } from '@/hooks/useSearchFilters'
import { useSearchParamsSchema } from '@/hooks/useSearchParamsSchema'
import { getGamesPageGameScore } from '@/lib/configs'
import { getApiSort, mapExploreRoomItemToRoom, replaceUrlWithQueryString } from '@/lib/search'
import { cn } from '@/lib/utils'
import { getCardType } from '@/lib/viverse-world-card'
import IconTool from '@/public/images/worlds/icon_tool.svg'
import type { Locale } from '@/server/constants/locales'
import type { ExploreRoomsQueryParams, PaginatedResponse, Room } from '@/services/cms'
import { exploreRooms } from '@/services/cms'
import type { RoomTag } from '@/types/genre'
import type { SortValue } from '@/types/search'

const GenreDeviceFilterModal = dynamic(
  () => import('@/app/[locale]/(worlds)/(worlds-landing)/search/components/GenreDeviceFilterModal'),
)

const CARD_GRID = 'grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6 1.5xl:grid-cols-4'
const DEFAULT_TAG_KEY = 'all-games'

const SORT_BI_EVENTS: Partial<Record<SortValue, string>> = {
  latest: BI_EVENT_LATEST,
  liked: BI_EVENT_MOST_LIKED,
  popular: BI_EVENT_MOST_VISITED,
  recommended: BI_EVENT_FEATURED,
  'release-date': BI_EVENT_RELEASE_DATE,
}

const getKey = (
  pageIndex: number,
  previousPageData: PaginatedResponse<Room> | null,
  activeTag: string,
  sort: string | null,
  browserLocale: string,
) => {
  if (!browserLocale) return null

  const params = new URLSearchParams({ [SEARCH_PARAM_KEYS.GENRE]: activeTag, start: '0' })
  if (sort) params.set(SEARCH_PARAM_KEYS.SORT, sort)

  if (pageIndex === 0) return `gameRooms?${params.toString()}`

  if (!previousPageData?.results) return null

  const next = previousPageData.pages?.next
  if (!next) return null

  const query = next.split('?')[1]
  const start = new URLSearchParams(query).get('start')

  params.set('start', start || '0')
  return `gameRooms?${params.toString()}`
}

const createFetcher = (htcToken: string | undefined, locale: Locale, browserLocale: string) => {
  return (key: string) => {
    const query = key.split('?')[1]
    const params = new URLSearchParams(query)
    const start = Number(params.get('start'))
    const tag = params.get(SEARCH_PARAM_KEYS.GENRE)
    const sort = params.get(SEARCH_PARAM_KEYS.SORT)

    const apiSort = getApiSort(sort, false)

    const requestParams: ExploreRoomsQueryParams = {
      limit: EXPLORE_GAMES_LIMIT,
      start,
      site_locale: locale,
      locale: browserLocale,
      tag: tag === DEFAULT_TAG_KEY ? '' : tag || undefined,
      exclude_persona: true,
      content_types: ['games'],
      game_score: getGamesPageGameScore(),
      sort: apiSort,
    }

    return exploreRooms(requestParams, htcToken, false).then(
      (res): PaginatedResponse<Partial<Room>> | undefined => {
        if (!res?.results) return undefined
        return {
          pages: res.results.pages,
          results: res.results.results.map(mapExploreRoomItemToRoom),
        }
      },
    )
  }
}

type ExploreGamesProps = {
  promotedRoomTagsPromise: Promise<RoomTag[]>
  gameGenreTagsPromise: Promise<RoomTag[]>
}

export default function ExploreGames({
  promotedRoomTagsPromise,
  gameGenreTagsPromise,
}: ExploreGamesProps) {
  const promotedRoomTags = use(promotedRoomTagsPromise)
  const gameGenreTags = use(gameGenreTagsPromise)

  const { store } = useConfigsContext()
  const { isIosMobileApp, isMobileApp } = useUAContext()
  const { logoHeight } = useLayoutContext()
  const { sendBIReport } = useBISdkContext()

  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hash = useHash()

  const { sort, genre } = useSearchParamsSchema()

  const saved = useSavedPages()

  const urlTag = genre || DEFAULT_TAG_KEY
  const [pendingTag, setPendingTag] = useState<string | null>(null)
  const activeTag = pendingTag ?? urlTag
  const isTagLoading = pendingTag !== null

  useEffect(() => {
    setPendingTag(null)
  }, [urlTag])
  const browserLocale = useBrowserLocale()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const htcToken = store?.credentials?.htcToken
  const filterModalTopOffset = isMobile ? logoHeight : undefined

  const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) =>
      getKey(pageIndex, previousPageData, activeTag, sort, browserLocale),
    createFetcher(htcToken, locale, browserLocale),
    {
      revalidateFirstPage: false,
      initialSize: saved.savedPages,
    },
  )

  const trackedSetSize = useScrollRestorationSWR(saved, { size, setSize, isLoading, isValidating })

  const t = useTranslations()

  const paginatedRooms = useMemo<Partial<Room>[]>(
    () => data?.flatMap((page) => page?.results ?? []) ?? [],
    [data],
  )

  const allRooms = useMemo(() => {
    return isIosMobileApp
      ? paginatedRooms.filter((r) => r.is_voucher_room !== true)
      : paginatedRooms
  }, [paginatedRooms, isIosMobileApp])

  const filterTags = useMemo(() => {
    return [
      {
        key: DEFAULT_TAG_KEY,
        label: t('worlds-landing.explore-games.all-games'),
      },
      ...promotedRoomTags.map((tag) => ({
        key: tag.name,
        label: tag.translations?.[0]?.name ?? tag.name,
      })),
    ]
  }, [promotedRoomTags, t])

  const {
    showFilters,
    setShowFilters,
    tag,
    updateSearchParams,
    handleFilterChange,
    handleClearAllFilters,
  } = useSearchFilters()

  const total = data?.[0]?.pages?.total || 0
  const hasNextPage = Boolean(data?.[data.length - 1]?.pages?.next)
  const enableLoadMore = hasNextPage && total > paginatedRooms.length

  const { show: showSkeletons, count: skeletonCount } = skeletonState(
    saved,
    { isLoading, isValidating },
    allRooms.length,
    EXPLORE_GAMES_LIMIT,
  )

  const loadMoreRef = useLoadMore({
    enabled: enableLoadMore,
    isLoading,
    isValidating,
    setSize: trackedSetSize,
    size,
  })

  const handleTagClick = (tag: string) => {
    if (isTagLoading || tag === activeTag) return

    const params = new URLSearchParams(searchParams.toString())
    if (tag !== DEFAULT_TAG_KEY) {
      params.set(SEARCH_PARAM_KEYS.GENRE, tag)
    } else {
      params.delete(SEARCH_PARAM_KEYS.GENRE)
    }

    const queryString = params.toString()
    if (queryString === searchParams.toString()) {
      setPendingTag(null)
      return
    }

    setPendingTag(tag)
    replaceUrlWithQueryString(router, pathname, queryString, hash, false)
  }

  const sortBy = (sort || 'recommended') as SortValue

  const gamesSortOptions = [
    { value: 'recommended' as const, label: t('worlds-landing.search.sort.recommended') },
    { value: 'release-date' as const, label: t('worlds-landing.search.sort.release-date') },
    { value: 'last_updated_at' as const, label: t('worlds-landing.search.sort.last-updated-at') },
  ]

  const handleSortChange = (value: SortValue) => {
    sendBIReport({
      ev: SORT_BI_EVENTS[value] || BI_EVENT_LATEST,
      category: BI_CATEGORY_SORT_AND_SEARCH,
    })

    updateSearchParams({ [SEARCH_PARAM_KEYS.SORT]: value })
  }

  const hasTopOffset = Number(filterModalTopOffset) > 0

  return (
    <section
      id="explore-games"
      data-section="explore-games"
      className={cn('flex scroll-mt-5 flex-col', isMobileApp ? 'gap-2 pt-0' : 'gap-[18px] pt-8')}
    >
      {!isMobileApp && (
        <h2 className="header-5 md:header-4 px-4 md:px-6">
          {t('worlds-landing.explore-games.title')}
        </h2>
      )}

      <div className="bg-background/80 sticky top-0 z-15 flex flex-col backdrop-blur-md">
        <div className="flex items-center justify-between max-lg:flex-wrap">
          {!isMobileApp && (
            <div
              data-component="explore-tags"
              className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-4 md:px-6 lg:px-6"
            >
              <ExploreViverseTags
                filterTags={filterTags}
                activeTag={activeTag}
                disabled={isTagLoading}
                setActiveTag={handleTagClick}
              />
            </div>
          )}
          <div
            data-component="explore-search-toolbar"
            className={cn(
              'flex items-center gap-3 px-4 max-md:w-full md:pr-11 md:pl-6',
              isMobileApp && 'py-3',
            )}
          >
            <SortByMenu
              sortBy={sortBy}
              onSortChange={handleSortChange}
              sortOptions={gamesSortOptions}
            />
            <ViverseButton
              variant="icon3"
              size="silent2"
              onClick={() => setShowFilters(true)}
              className={cn('flex min-w-fit items-center gap-2', {
                'bg-vive-primary-60 text-white': showFilters,
              })}
            >
              <IconTool className="size-4" />
              {t('worlds-landing.search.filters')}
            </ViverseButton>
          </div>
        </div>

        <Separator
          className={cn('bg-vive-surface-normal lg:mt-0', isMobileApp ? 'mt-0' : 'mt-4')}
        />
      </div>

      <GenreDeviceFilterModal
        open={showFilters}
        onOpenChange={setShowFilters}
        tag={tag}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAllFilters}
        isSearchPage
        showDeviceSection={false}
        title={t('worlds-landing.search.genre.title')}
        filterTags={gameGenreTags}
        contentClassName="lg:max-h-fit"
        {...(hasTopOffset && {
          contentStyle: {
            marginTop: `${filterModalTopOffset}px`,
            maxHeight: `calc(100dvh - ${filterModalTopOffset}px)`,
          },
        })}
      />

      <div className="px-4 md:px-6">
        {!showSkeletons && allRooms.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <IconGamepad className="size-8 stroke-white" strokeWidth={1.5} />
            <h3 className="subheader-2 mt-2 font-semibold text-white">
              {t('worlds-landing.explore-games.empty-state.title')}
            </h3>
            <p className="subheader-3 text-vive-primary-30 mt-1 font-semibold">
              {t('worlds-landing.explore-games.empty-state.description')}
            </p>
          </div>
        ) : (
          <>
            <div className={CARD_GRID}>
              {allRooms.map((world) => (
                <ViverseWorldCard
                  key={world.id}
                  info={world}
                  data-room-id={world.id ? `eg:${world.id}` : undefined}
                  className="h-auto p-0"
                  imageClassName="aspect-video w-full object-cover"
                  hideLiked={!htcToken}
                  hidePremiumBadge={false}
                  cardType={getCardType(world.content_type)}
                />
              ))}
              {showSkeletons &&
                Array.from({ length: skeletonCount }).map((_, index) => (
                  <ViverseWorldCardSkeleton key={`skeleton-${index}`} className="h-auto p-0" />
                ))}
            </div>
            {enableLoadMore && <div ref={loadMoreRef} className="h-1" />}
          </>
        )}
      </div>
    </section>
  )
}

export function ExploreGamesSkeleton() {
  const t = useTranslations()
  const skeletonCount = useFallbackSkeletons(EXPLORE_GAMES_LIMIT)
  const { isMobileApp } = useUAContext()

  return (
    <section className={cn('flex flex-col', isMobileApp ? 'gap-2 pt-0' : 'gap-[18px] pt-8')}>
      {!isMobileApp && (
        <h2 className="header-5 md:header-4 px-4 md:px-6">
          {t('worlds-landing.explore-games.title')}
        </h2>
      )}

      {!isMobileApp && (
        <div className="flex flex-col gap-2">
          <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-4 md:flex-wrap md:overflow-x-hidden md:px-6 lg:px-6">
            <ExploreViverseTagsSkeleton />
          </div>
          <Separator className="bg-vive-surface-normal" />
        </div>
      )}

      <div className="px-4 md:px-6">
        <div className={CARD_GRID}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <ViverseWorldCardSkeleton key={`skeleton-${index}`} className="h-auto p-0" />
          ))}
        </div>
      </div>
    </section>
  )
}
